import { Text } from '@libs/ui/ds'
import { Controller } from '@libs/ui/form'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import type { RestIterable } from '@spa/constants/RestTypes'
import RestTypes from '@spa/constants/RestTypes'
import { orderAlphabetically } from '@spa/utils/orderAlphabetically'
import { groupBy } from 'lodash-es'
import { useEffect } from 'react'
import type { FC } from 'react'
import type { Control } from 'react-hook-form'

import { InfoReplacingCompensatoryRest } from '../Alerts'

type Props = {
  restIsLocked: boolean
  restTypeOptions: RestIterable
  isCustom: boolean
  setValue: Function
  control: Control
  disabled?: boolean
  restType: string
  selectedContracts: {
    label: string
    value: number
  }[]
  rightComponent?: string
}

export const RestTypeField: FC<Props> = ({
  restTypeOptions,
  restIsLocked,
  isCustom,
  control,
  rightComponent,
  disabled,
  setValue,
  restType,
  selectedContracts
}) => {
  const { t, tCommon } = usePlanningFormTranslation()
  const groupedRests = groupBy(restTypeOptions.toArray(), 'is_most_used')
  const sortedRestTypes = Object.keys(groupedRests).map(i =>
    groupedRests[i].sort((firstRest, secRest) =>
      firstRest.name.localeCompare(secRest.name)
    )
  )

  const restTypesSelectData = sortedRestTypes.flatMap((rests, index) => {
    const group =
      index === 0 ? t('restForm.mostUsedRests') : t('restForm.allRests')

    const restsData = rests.map(rest => ({
      name: tCommon(rest.name),
      id: rest.id
    }))

    const orderedRests = orderAlphabetically(restsData)

    return orderedRests.map(rest => ({
      label: rest.name,
      value: rest.id,
      group
    }))
  })

  const canBeBulked = RestTypes.get(String(restType))?.can_be_bulked
  useEffect(() => {
    const [firstContract] = selectedContracts
    if (!canBeBulked && Boolean(firstContract)) {
      setValue('user_contract_ids', [firstContract], { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canBeBulked])

  return (
    <div>
      <Controller.Select
        control={control}
        data-testid='field-select-rest-type'
        name='rest_type.value'
        options={restTypesSelectData}
        clearable
        disabled={restIsLocked || isCustom || disabled}
        searchable
        dropdownWidth='target'
        searchAdapter='fuzzyIncludingGroup'
        placeholder={t('restForm.restTypePlaceholder')}
        label={<Text className='w-max'>{t('restForm.restType')}</Text>}
        labelRightSection={
          <Text className='w-full text-right'>{rightComponent}</Text>
        }
      />

      {restType === RestTypes.get('compensatory_rest').id && (
        <InfoReplacingCompensatoryRest />
      )}
    </div>
  )
}
